import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Toggle } from "@/components/ui/toggle";
import { Loader2 } from 'lucide-react';
import { cn } from "@/lib/utils";
import { axiosInstance } from '@/utils/axiosConfig';
import { authStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { showToast } from '@/utils/toast';

// Move all interfaces
export interface Application {
  id: string;
  name: string;
  description: string;
  slug: string;
  modules: Module[];
  supportsMemberLicensing: boolean;
  supportsDeviceLicensing: boolean;
  supportsModuleLicensing: boolean;
}

interface Module {
  id: string;
  name: string;
  description: string;
  key: string;
}

export interface OrganizationApplication {
  id: string;
  maxMembers: number | null;
  maxDevices: number | null;
  isActive: boolean;
  application: Application;
  activeModules: {
    module: Module;
  }[];
}

interface ModuleAction {
  orgApp: OrganizationApplication;
  module: Module;
  isActive: boolean;
}

interface SubscribeAction {
  app: Application;
  moduleIds: string[];
  maxMembers: number | null;
  maxDevices: number | null;
}

interface LicenseEdit {
  orgApp: OrganizationApplication;
  type: 'members' | 'devices';
  currentValue: number | null;
}

interface ApplicationsProps {
  applications: Application[];
  orgApplications: OrganizationApplication[];
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  setOrgApplications: (apps: OrganizationApplication[]) => void;
  setLoading: (loading: boolean) => void;
}

// Add permission check function
const canManageSubscriptions = (userRole?: string) => {
  return userRole === 'OWNER';
};

export const Applications = ({
  applications,
  orgApplications,
  loading,
  error,
  setError,
  setOrgApplications,
  setLoading
}: ApplicationsProps) => {
  const { t } = useTranslation();
  // Move all state
  const [unsubscribeApp, setUnsubscribeApp] = useState<OrganizationApplication | null>(null);
  const [selectedModules, setSelectedModules] = useState<Record<string, Set<string>>>({});
  const [moduleActionConfirm, setModuleActionConfirm] = useState<ModuleAction | null>(null);
  const [subscribeConfirm, setSubscribeConfirm] = useState<SubscribeAction | null>(null);
  const [licenseEdit, setLicenseEdit] = useState<LicenseEdit | null>(null);

  const handleModuleToggle = (appId: string, moduleId: string) => {
    setSelectedModules(prev => {
      const appModules = prev[appId] || new Set();
      const newModules = new Set(appModules);
      
      if (newModules.has(moduleId)) {
        newModules.delete(moduleId);
      } else {
        newModules.add(moduleId);
      }

      return {
        ...prev,
        [appId]: newModules
      };
    });
  };

  const handleSubscribe = async (app: Application) => {
    const moduleIds = Array.from(selectedModules[app.id] || new Set());
    setSubscribeConfirm({
      app,
      moduleIds,
      maxMembers: null,
      maxDevices: null
    });
  };

  const confirmSubscribe = async () => {
    if (!subscribeConfirm) return;
    
    try {
      const orgId = authStore.currentOrganization?.id;
      if (!orgId) {
        setError(t('applications.selectOrganization'));
        return;
      }

      setLoading(true);
      await axiosInstance.post(
        `/applications/organizations/${orgId}/applications/${subscribeConfirm.app.id}`,
        {
          maxMembers: subscribeConfirm.maxMembers,
          maxDevices: subscribeConfirm.maxDevices,
          moduleIds: subscribeConfirm.moduleIds
        }
      );

      const response = await axiosInstance.get(`/applications/organizations/${orgId}`);
      setOrgApplications(response.data);
      
      setSelectedModules(prev => {
        const newState = { ...prev };
        delete newState[subscribeConfirm.app.id];
        return newState;
      });
      
      setError(null);
      showToast.success({
        description: t('applications.successfullySubscribed')
      });
    } catch (error: any) {
      console.error('Error subscribing to application:', error);
      showToast.error({
        description: error.response?.data?.message || t('applications.failedToSubscribe')
      });
    } finally {
      setLoading(false);
      setSubscribeConfirm(null);
    }
  };

  const handleUnsubscribe = async (orgApp: OrganizationApplication) => {
    try {
      const orgId = authStore.currentOrganization?.id;
      if (!orgId) return;

      setLoading(true);
      await axiosInstance.delete(
        `/applications/organizations/${orgId}/applications/${orgApp.application.id}`
      );

      // Refresh organization applications
      const response = await axiosInstance.get(`/applications/organizations/${orgId}`);
      setOrgApplications(response.data);
      setError(null);
      showToast.success({
        description: t('applications.successfullyUnsubscribed')
      });
    } catch (error: any) {
      console.error('Error unsubscribing from application:', error);
      showToast.error({
        description: error.response?.data?.message || t('applications.failedToUnsubscribe')
      });
    } finally {
      setLoading(false);
      setUnsubscribeApp(null);
    }
  };

  const handleModuleActivation = (orgApp: OrganizationApplication, module: Module, isActive: boolean) => {
    setModuleActionConfirm({ orgApp, module, isActive });
  };

  const confirmModuleAction = async () => {
    if (!moduleActionConfirm) return;
    const { orgApp, module, isActive } = moduleActionConfirm;

    try {
      const orgId = authStore.currentOrganization?.id;
      if (!orgId) return;

      setLoading(true);
      
      const currentModuleIds = orgApp.activeModules.map(am => am.module.id);
      const newModuleIds = isActive
        ? currentModuleIds.filter(id => id !== module.id)
        : [...currentModuleIds, module.id];

      await axiosInstance.put(
        `/applications/organizations/${orgId}/applications/${orgApp.application.id}`,
        {
          maxMembers: orgApp.maxMembers,
          maxDevices: orgApp.maxDevices,
          moduleIds: newModuleIds,
          isActive: orgApp.isActive
        }
      );

      const response = await axiosInstance.get(`/applications/organizations/${orgId}`);
      setOrgApplications(response.data);
      setError(null);
      showToast.success({
        description: isActive ? t('applications.moduleDeactivated') : t('applications.moduleActivated')
      });
    } catch (error: any) {
      console.error('Error toggling module:', error);
      showToast.error({
        description: error.response?.data?.message || (moduleActionConfirm?.isActive ? t('applications.failedToDeactivateModule') : t('applications.failedToActivateModule'))
      });
    } finally {
      setLoading(false);
      setModuleActionConfirm(null);
    }
  };

  const handleLicenseUpdate = async () => {
    if (!licenseEdit) return;
    
    try {
      const orgId = authStore.currentOrganization?.id;
      if (!orgId) return;

      setLoading(true);
      const { orgApp, type, currentValue } = licenseEdit;

      await axiosInstance.put(
        `/applications/organizations/${orgId}/applications/${orgApp.application.id}`,
        {
          maxMembers: type === 'members' ? currentValue : orgApp.maxMembers,
          maxDevices: type === 'devices' ? currentValue : orgApp.maxDevices,
          moduleIds: orgApp.activeModules.map(am => am.module.id),
          isActive: orgApp.isActive
        }
      );

      // Refresh organization applications
      const response = await axiosInstance.get(`/applications/organizations/${orgId}`);
      setOrgApplications(response.data);
      setError(null);
    } catch (error: any) {
      console.error('Error updating license:', error);
      showToast.error({
        description: error.response?.data?.message || t('applications.failedToUpdateLicense')
      });
    } finally {
      setLoading(false);
      setLicenseEdit(null);
    }
  };

  return (
    <div className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}

        {/* Active Applications */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">{t('applications.title')}</h2>
          {orgApplications.length === 0 ? (
            <Card className="p-6">
              <p className="text-gray-600">{t('applications.noActiveApplications')}</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {orgApplications.map((orgApp) => (
                <Card key={orgApp.id} className="flex flex-col p-6">
                  {/* Header Section */}
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h3 className="font-semibold">{orgApp.application.name}</h3>
                      <p className="text-sm text-gray-600">{orgApp.application.description}</p>
                    </div>
                    <Badge variant={orgApp.isActive ? "success" : "destructive"}>
                      {orgApp.isActive ? t('applications.active') : t('applications.inactive')}
                    </Badge>
                  </div>

                  {/* Content Section - Using flex-1 to take remaining space */}
                  <div className="flex-1 flex flex-col">
                    {/* License Limits Section */}
                    <div className="space-y-2 mb-6">
                      {orgApp.application.supportsMemberLicensing && (
                        <div className="flex items-center justify-between">
                          <p className="text-sm">
                            <span className="font-medium">{t('applications.members')}</span>{' '}
                            {orgApp.maxMembers === null ? t('applications.unlimited') : orgApp.maxMembers}
                          </p>
                          {canManageSubscriptions(authStore.currentOrganization?.role) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setLicenseEdit({
                                orgApp,
                                type: 'members',
                                currentValue: orgApp.maxMembers
                              })}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                      
                      {orgApp.application.supportsDeviceLicensing && (
                        <div className="flex items-center justify-between">
                          <p className="text-sm">
                            <span className="font-medium">{t('applications.devices')}</span>{' '}
                            {orgApp.maxDevices === null ? t('applications.unlimited') : orgApp.maxDevices}
                          </p>
                          {canManageSubscriptions(authStore.currentOrganization?.role) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setLicenseEdit({
                                orgApp,
                                type: 'devices',
                                currentValue: orgApp.maxDevices
                              })}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Spacer to push Modules section to bottom */}
                    <div className="flex-1" />

                    {/* Modules Section */}
                    <div className="mt-auto">
                      <h4 className="text-sm font-medium mb-2">{t('applications.manageModules')}:</h4>
                      <div className="flex flex-wrap gap-2">
                        {orgApp.application.modules.map((module) => {
                          const isActive = orgApp.activeModules.some(am => am.module.id === module.id);
                          return (
                            <Toggle
                              key={module.id}
                              size="sm"
                              variant="outline"
                              pressed={isActive}
                              onPressedChange={() => 
                                canManageSubscriptions(authStore.currentOrganization?.role) 
                                  ? handleModuleActivation(orgApp, module, isActive)
                                  : null
                              }
                              disabled={loading || !canManageSubscriptions(authStore.currentOrganization?.role)}
                              className={cn(
                                "data-[state=on]:bg-primary data-[state=on]:text-primary-foreground",
                                (loading || !canManageSubscriptions(authStore.currentOrganization?.role)) && "opacity-50 cursor-not-allowed"
                              )}
                            >
                              <div className="flex items-center gap-1">
                                {module.name}
                                {loading && <Loader2 className="h-3 w-3 animate-spin" />}
                              </div>
                            </Toggle>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Footer Section - Always at bottom */}
                  <div className="pt-8 mt-6 border-t">
                    <div className="flex justify-end">
                      {canManageSubscriptions(authStore.currentOrganization?.role) && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setUnsubscribeApp(orgApp)}
                        >
                          {t('applications.unsubscribeFrom')}
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Available Applications */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">{t('applications.availableApplications')}</h2>
          {applications.length === 0 ? (
            <Card className="p-6">
              <p className="text-gray-600">{t('applications.noApplicationsAvailable')}</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {applications
                .filter(app => !orgApplications.some(orgApp => orgApp.application.id === app.id))
                .map((app) => (
                  <Card key={app.id} className="p-6 space-y-4">
                    <div>
                      <h3 className="font-semibold">{app.name}</h3>
                      <p className="text-sm text-gray-600">{app.description}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium mb-2">{t('applications.selectModules')}:</h4>
                      <div className="flex flex-wrap gap-2">
                        {app.modules.map((module) => (
                          <Toggle
                            key={module.id}
                            size="sm"
                            variant="outline"
                            pressed={selectedModules[app.id]?.has(module.id) || false}
                            onPressedChange={() => handleModuleToggle(app.id, module.id)}
                            className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                          >
                            {module.name}
                          </Toggle>
                        ))}
                      </div>
                    </div>

                    <Button 
                      className="w-full"
                      onClick={() => handleSubscribe(app)}
                      disabled={loading || !authStore.currentOrganization || !(selectedModules[app.id]?.size > 0)}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('applications.subscribing')}
                        </>
                      ) : (
                        t('applications.subscribeTo')
                      )}
                    </Button>
                  </Card>
                ))}
            </div>
          )}
        </div>

        <AlertDialog open={!!unsubscribeApp} onOpenChange={() => setUnsubscribeApp(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('applications.unsubscribeFrom')} {unsubscribeApp?.application.name}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('applications.unsubscribeConfirmation')}
                {t('applications.unsubscribeWarning')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => unsubscribeApp && handleUnsubscribe(unsubscribeApp)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('applications.unsubscribing')}
                  </>
                ) : (
                  t('applications.unsubscribeFrom')
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Module Action Confirmation Dialog */}
        <AlertDialog 
          open={!!moduleActionConfirm} 
          onOpenChange={() => setModuleActionConfirm(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {moduleActionConfirm?.isActive ? 'Deactivate' : 'Activate'} {moduleActionConfirm?.module.name}
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to {moduleActionConfirm?.isActive ? 'deactivate' : 'activate'} the{' '}
                {moduleActionConfirm?.module.name} module? 
                {moduleActionConfirm?.isActive 
                  ? ' This will disable access to this module\'s features.'
                  : ' This will enable access to this module\'s features.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmModuleAction}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {moduleActionConfirm?.isActive ? 'Deactivating...' : 'Activating...'}
                  </>
                ) : (
                  moduleActionConfirm?.isActive ? 'Deactivate' : 'Activate'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Subscribe Confirmation Dialog */}
        <AlertDialog 
          open={!!subscribeConfirm} 
          onOpenChange={() => setSubscribeConfirm(null)}
        >
          <AlertDialogContent className="max-w-xl">
            <AlertDialogHeader>
              <AlertDialogTitle>
                Subscribe to {subscribeConfirm?.app.name}
              </AlertDialogTitle>
              <div className="space-y-4">
                <AlertDialogDescription>
                  You are about to subscribe to {subscribeConfirm?.app.name}.
                </AlertDialogDescription>

                {/* Licensing Options */}
                {subscribeConfirm && (
                  <div className="space-y-4 pt-4">
                    {subscribeConfirm.app.supportsMemberLicensing && (
                      <div className="space-y-2">
                        <label htmlFor="maxMembers" className="text-sm font-medium">
                          Maximum Members
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            id="maxMembers"
                            min="0"
                            placeholder="Unlimited"
                            value={subscribeConfirm.maxMembers || ''}
                            onChange={(e) => setSubscribeConfirm(prev => prev ? {
                              ...prev,
                              maxMembers: e.target.value ? Number(e.target.value) : null
                            } : null)}
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setSubscribeConfirm(prev => prev ? {
                              ...prev,
                              maxMembers: null
                            } : null)}
                          >
                            Unlimited
                          </Button>
                        </div>
                      </div>
                    )}

                    {subscribeConfirm.app.supportsDeviceLicensing && (
                      <div className="space-y-2">
                        <label htmlFor="maxDevices" className="text-sm font-medium">
                          Maximum Devices
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            id="maxDevices"
                            min="0"
                            placeholder="Unlimited"
                            value={subscribeConfirm.maxDevices || ''}
                            onChange={(e) => setSubscribeConfirm(prev => prev ? {
                              ...prev,
                              maxDevices: e.target.value ? Number(e.target.value) : null
                            } : null)}
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setSubscribeConfirm(prev => prev ? {
                              ...prev,
                              maxDevices: null
                            } : null)}
                          >
                            Unlimited
                          </Button>
                        </div>
                      </div>
                    )}

                    {subscribeConfirm.app.supportsModuleLicensing && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium">Selected Modules:</h4>
                        <ul className="list-disc list-inside">
                          {subscribeConfirm.moduleIds.map(moduleId => {
                            const module = subscribeConfirm.app.modules.find(m => m.id === moduleId);
                            return module && (
                              <li key={moduleId} className="text-sm text-gray-600 ml-2">
                                {module.name}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmSubscribe}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  'Subscribe'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* License Edit Dialog */}
        <AlertDialog 
          open={!!licenseEdit} 
          onOpenChange={() => setLicenseEdit(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Edit {licenseEdit?.type === 'members' ? 'Member' : 'Device'} Limit
              </AlertDialogTitle>
              <div className="space-y-4">
                <AlertDialogDescription>
                  Update the maximum number of {licenseEdit?.type === 'members' ? 'members' : 'devices'} for {licenseEdit?.orgApp.application.name}.
                </AlertDialogDescription>

                <div className="space-y-2">
                  <label htmlFor="licenseLimit" className="text-sm font-medium">
                    Maximum {licenseEdit?.type === 'members' ? 'Members' : 'Devices'}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      id="licenseLimit"
                      min="0"
                      placeholder="Unlimited"
                      value={licenseEdit?.currentValue || ''}
                      onChange={(e) => setLicenseEdit(prev => prev ? {
                        ...prev,
                        currentValue: e.target.value ? Number(e.target.value) : null
                      } : null)}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setLicenseEdit(prev => prev ? {
                        ...prev,
                        currentValue: null
                      } : null)}
                    >
                      Unlimited
                    </Button>
                  </div>
                </div>
              </div>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleLicenseUpdate}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
  );
}; 